import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { api } from '@/App';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Pill, Upload, Filter, Users } from 'lucide-react';

export default function ItemsTab() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ vendor_id: '', name: '', sku: '', units: '', description: '' });
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [showDisableWarning, setShowDisableWarning] = useState(false);
  const [itemToToggle, setItemToToggle] = useState(null);
  const [pushToAllUsers, setPushToAllUsers] = useState(false);
  const [selectedVendorFilter, setSelectedVendorFilter] = useState('all');
  const [showPushWarning, setShowPushWarning] = useState(false);
  const [itemToPush, setItemToPush] = useState(null);
  const [pushing, setPushing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [itemsRes, vendorsRes] = await Promise.all([
        api.get('/master-items'),
        api.get('/vendors')
      ]);
      setItems(itemsRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/master-items/${editingId}`, formData);
        toast.success('Item updated');
      } else {
        // For creating new items, include pushToAllUsers parameter
        await api.post('/master-items', { ...formData, pushToAllUsers });
        if (pushToAllUsers) {
          toast.success('Item created and pushed to all users');
        } else {
          toast.success('Item created');
        }
      }
      setOpen(false);
      setFormData({ vendor_id: '', name: '', sku: '', units: '', description: '' });
      setPushToAllUsers(false);
      setEditingId(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({ 
      vendor_id: item.vendor_id, 
      name: item.name, 
      sku: item.sku || '', 
      units: item.units || '', 
      description: item.description 
    });
    setOpen(true);
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post('/master-items/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.errors && res.data.errors.length > 0) {
        toast.warning(`Imported ${res.data.imported} items with ${res.data.errors.length} errors`);
        console.log('Import errors:', res.data.errors);
      } else {
        toast.success(`Successfully imported ${res.data.imported} items`);
      }
      
      loadData();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleToggleEnabled = (item) => {
    if (item.enabled && item.enabled !== false) {
      setItemToToggle(item);
      setShowDisableWarning(true);
    } else {
      confirmToggleEnabled(item, true);
    }
  };

  const confirmToggleEnabled = async (item, newEnabledStatus) => {
    try {
      await api.put(`/master-items/${item.id}`, {
        ...item,
        enabled: newEnabledStatus
      });
      
      if (newEnabledStatus) {
        toast.success(`${item.name} enabled. Users must manually re-enable it via "Manage Items".`);
      } else {
        toast.success(`${item.name} disabled for all users.`);
      }
      
      loadData();
      setShowDisableWarning(false);
      setItemToToggle(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update item');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await api.delete(`/master-items/${id}`);
      toast.success('Item deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handlePushToUsers = (item) => {
    setItemToPush(item);
    setShowPushWarning(true);
  };

  const confirmPushToUsers = async () => {
    if (!itemToPush) return;
    
    setPushing(true);
    try {
      await api.post(`/master-items/${itemToPush.id}/push-to-users`);
      toast.success(`${itemToPush.name} has been enabled for all users`);
      setShowPushWarning(false);
      setItemToPush(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push item to users');
    } finally {
      setPushing(false);
    }
  };

  // Filter items by selected vendor
  const filteredItems = selectedVendorFilter === 'all' 
    ? items 
    : items.filter(item => item.vendor_id === selectedVendorFilter);

  // Get vendor name for display
  const getVendorName = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor ? vendor.name : 'Unknown';
  };

  // Sort filtered items by name
  const sortedItems = [...filteredItems].sort((a, b) => a.name.localeCompare(b.name));

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <>
      <Card className="shadow-lg border-0" data-testid="items-card">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-2xl flex items-center space-x-2">
                <Pill className="w-6 h-6 text-blue-600" />
                <span>Master Items</span>
              </CardTitle>
              <CardDescription>Manage medicine catalog</CardDescription>
            </div>
            <div className="flex space-x-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCSVImport}
                className="hidden"
                data-testid="csv-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                variant="outline"
                className="hover:bg-green-50 hover:border-green-300"
                data-testid="import-csv-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                {importing ? 'Importing...' : 'Import CSV'}
              </Button>
              <Dialog open={open} onOpenChange={(isOpen) => {
                setOpen(isOpen);
                if (!isOpen) {
                  setEditingId(null);
                  setFormData({ vendor_id: '', name: '', sku: '', units: '', description: '' });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg" data-testid="add-item-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </DialogTrigger>
              <DialogContent data-testid="item-dialog">
                <form onSubmit={handleSubmit}>
                  <DialogHeader>
                    <DialogTitle>{editingId ? 'Edit Item' : 'Add Item'}</DialogTitle>
                    <DialogDescription>Enter item details</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label htmlFor="vendor">Vendor</Label>
                      <Select
                        value={formData.vendor_id}
                        onValueChange={(value) => setFormData({ ...formData, vendor_id: value })}
                        required
                      >
                        <SelectTrigger className="mt-2" data-testid="vendor-select">
                          <SelectValue placeholder="Select vendor" />
                        </SelectTrigger>
                        <SelectContent>
                          {vendors.map((vendor) => (
                            <SelectItem key={vendor.id} value={vendor.id} data-testid={`vendor-option-${vendor.id}`}>
                              {vendor.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="sku">Item SKU</Label>
                      <Input
                        id="sku"
                        value={formData.sku}
                        onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                        placeholder="e.g., ASP-500"
                        data-testid="item-sku-input"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="name">Item Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Aspirin 500mg"
                        required
                        data-testid="item-name-input"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="units">Units</Label>
                      <Input
                        id="units"
                        value={formData.units}
                        onChange={(e) => setFormData({ ...formData, units: e.target.value })}
                        placeholder="e.g., Box of 100, Bottle"
                        data-testid="item-units-input"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">Description (Optional)</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Item description..."
                        data-testid="item-description-input"
                        className="mt-2"
                      />
                    </div>
                    
                    {/* Push to All Users Toggle - Only show when creating new items */}
                    {!editingId && (
                      <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <Switch
                          id="push-to-users"
                          checked={pushToAllUsers}
                          onCheckedChange={setPushToAllUsers}
                          data-testid="push-to-users-toggle"
                          className="data-[state=checked]:bg-blue-500"
                        />
                        <div className="flex-1">
                          <Label htmlFor="push-to-users" className="cursor-pointer font-medium text-blue-900">
                            Push to all users
                          </Label>
                          <p className="text-sm text-blue-700 mt-1">
                            When enabled, this item will be automatically added to all existing users' item lists.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="save-item-button">
                      {editingId ? 'Update' : 'Create'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Vendor Filter Dropdown */}
          <div className="mb-6 flex items-center space-x-3">
            <Filter className="w-5 h-5 text-gray-500" />
            <Label className="text-sm font-medium">Filter by Vendor:</Label>
            <Select
              value={selectedVendorFilter}
              onValueChange={setSelectedVendorFilter}
            >
              <SelectTrigger className="w-64" data-testid="vendor-filter-select">
                <SelectValue placeholder="Select vendor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vendors ({items.length} items)</SelectItem>
                {vendors.map((vendor) => {
                  const vendorItemCount = items.filter(i => i.vendor_id === vendor.id).length;
                  return (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.name} ({vendorItemCount} items)
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Pill className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">No items yet</p>
              <p className="text-sm">Add your first item to get started</p>
            </div>
          ) : sortedItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">No items found for selected vendor</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                  <span className="w-1.5 h-6 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full"></span>
                  <span>
                    {selectedVendorFilter === 'all' 
                      ? 'All Items' 
                      : getVendorName(selectedVendorFilter)}
                  </span>
                  <span className="text-sm font-normal text-gray-600">({sortedItems.length} items)</span>
                </h3>
              </div>
              <div className="divide-y divide-gray-200">
                {sortedItems.map((item) => (
                  <div
                    key={item.id}
                    className="px-4 py-3 hover:bg-gray-50 transition-colors"
                    data-testid={`item-row-${item.id}`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1 grid grid-cols-5 gap-4">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                          {item.description && (
                            <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                          )}
                        </div>
                        {selectedVendorFilter === 'all' && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Vendor</p>
                            <p className="text-sm text-gray-900 mt-1">{getVendorName(item.vendor_id)}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">SKU</p>
                          <p className="text-sm text-gray-900 mt-1">{item.sku || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Units</p>
                          <p className="text-sm text-gray-900 mt-1">{item.units || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Global Status</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <Switch
                              checked={item.enabled !== false}
                              onCheckedChange={() => handleToggleEnabled(item)}
                              data-testid={`toggle-enabled-${item.id}`}
                              className="data-[state=checked]:bg-green-500"
                            />
                            <span className={`text-xs ${item.enabled !== false ? 'text-green-600' : 'text-red-600'}`}>
                              {item.enabled !== false ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Push</p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePushToUsers(item)}
                            data-testid={`push-item-${item.id}`}
                            className="mt-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                          >
                            <Users className="w-4 h-4 mr-1" />
                            Push
                          </Button>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(item)}
                          data-testid={`edit-item-${item.id}`}
                          className="hover:bg-blue-50"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(item.id)}
                          data-testid={`delete-item-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disable Warning Dialog */}
      <AlertDialog open={showDisableWarning} onOpenChange={setShowDisableWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Item for All Users</AlertDialogTitle>
            <AlertDialogDescription>
              You're about to disable "{itemToToggle?.name}" for ALL users. This will immediately 
              disable this item in everyone's item list.
              <br/><br/>
              <strong>Note:</strong> When you enable it again later, users will have to manually 
              re-enable it via "Manage Items" on their user page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => confirmToggleEnabled(itemToToggle, false)}
              className="bg-red-600 hover:bg-red-700"
            >
              Disable for All Users
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Push to Users Warning Dialog */}
      <AlertDialog open={showPushWarning} onOpenChange={setShowPushWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Push Item to All Users</AlertDialogTitle>
            <AlertDialogDescription>
              You're about to enable "<strong>{itemToPush?.name}</strong>" for ALL users who can see this item based on their hospital's vendor settings.
              <br/><br/>
              This will add and enable this item in their item lists if not already assigned, or re-enable it if previously disabled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pushing}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmPushToUsers}
              disabled={pushing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {pushing ? 'Pushing...' : 'Push to All Users'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
